import { padStart } from 'lodash-es'
import axios from 'axios'
import { fabric } from 'fabric'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, merchConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import type Merch from '@/modules/merch/services/merch'
import utils from '@/services/utils'
import graphicTeeTechSheetImage from '@/assets/templateLayoutImages/graphicTeeTechSheet.png'

class GraphicTeeTechSheet implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.graphicTeeTechSheet
  name = 'Graphic Tee Tech Sheet'
  layoutImage = graphicTeeTechSheetImage
  slideWidth: number = 1280
  slideHeight: number = 720
  imgSize: number = 300
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  logoWidth = 155
  logoHeight = 87
  logoScaling = 0.20
  uaNameLogoWidth = 155
  uaNameLogoHeight = 15
  uaNameLogoScaling = 1
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  rightMargin = 20
  imageDefaultScaleFactor: number = merchConstants.slideImageDefaultScaleFactors.large
  leftPadding: number = 50
  rightPadding: number = 30
  topPadding: number = 50
  bottomPadding: number = 50
  uaHeaderIconLogoData: string = ''
  uaHeaderNameLogoData: string = ''

  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    bottomPadding: 30,
    fontSize: 34,
    width: 325,
    fontWeight: 'bold',
    rightPadding: 50,
    height: 40,
  }

  modelDetailsProps = {
    padding: 17,
    fontSize: 12,
    width: 325,
    fontWeight: 'bold',
  }

  headerImageNameLog = {
    bottomPadding: 180,
  }

  getOptions(): IMerchTemplateOption[] {
    // const availableSlideSizes: IKeyLabel[] = []
    // for (const key in merchConstants.slideSize) {
    //   availableSlideSizes.push({ key, label: merchConstants.slideSize[key] })
    // }
    // return [{
    //   name: 'slideSize',
    //   label: 'generateDialog.steps.options.slideSize',
    //   type: 'list',
    //   default: 'wideScreen',
    //   required: true,
    //   multiple: false,
    //   data: availableSlideSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // }]
    return []
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    const mapping: IMerchTemplateFileMapping[] = []

    mapping.push({
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code', 'colorwaynumber', 'colorway number'],
    })
    return mapping
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined): Promise<(void | number)> {
    const [uaHeaderLogoData, uaHeaderNameData]: any = await this.getHeaderLogosData([
      appConstants.logoPaths.uaIconWhite,
      appConstants.logoPaths.uaNameLogoWhite,
    ])

    this.uaHeaderIconLogoData = uaHeaderLogoData
    this.uaHeaderNameLogoData = uaHeaderNameData
    return await this.buildTemplate(merch, articlesData, options)
  }

  async buildTemplate(merch: Merch | undefined, articles: MyArticle[], options: Record<string, any>) {
    const promises: Promise<void | number>[] = []
    const slideSize = options.slideSize
    this.imgSize = this.imageWidth * this.imageDefaultScaleFactor

    this.changePropsAsPerSlideSize(slideSize)

    let ttlSlides = 0

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    let slideObjects: any[] = []
    for (const firstArticle of uniqueModelArticles) {
      slideObjects.push(await this.createHeaderLogo(
        this.leftPadding + (this.logoWidth * this.logoScaling) / 2,
        this.topPadding + (this.logoHeight * this.logoScaling) / 2,
      ))

      slideObjects.push(await this.createHeaderNameLogo(
        this.slideHeight - this.headerImageNameLog.bottomPadding + (this.uaNameLogoWidth * this.uaNameLogoScaling) / 2,
        this.leftPadding,
      ))

      const imgLeft = this.slideWidth - this.imgSize / 2 - this.bottomPadding
      const imgTop = this.slideHeight - this.imgSize / 2 - this.bottomPadding
      slideObjects.push(this.createImage(firstArticle.Id, imgLeft, imgTop, this.imageDefaultScaleFactor, this.imageDefaultScaleFactor))

      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, this.modelNameProps.width, this.font, this.modelNameProps.fontSize, this.modelNameProps.fontWeight)
      const modelNameTop = this.slideHeight / 2 - modelNameHeight - this.modelNameProps.bottomPadding
      const modelNameLeft = this.slideWidth - this.modelNameProps.width - this.rightMargin
      const modelNameTextObject = this.createLabel(
        firstArticle.Id,
        ['ModelName'],
        '',
        modelNameTop,
        modelNameLeft,
        this.modelNameProps.width,
        0,
        this.modelNameProps.fontSize,
        this.modelNameProps.fontWeight,
        'left',
        '#ffffff',
      )
      slideObjects.push(modelNameTextObject)

      const modelDetailsTop = modelNameTop + modelNameHeight + this.modelDetailsProps.padding
      const modelDetailsLeft = modelNameLeft
      const modelDetailsTextObject = this.createLabel(
        firstArticle.Id,
        ['ModelNumber', '_RetailPrice'],
        ' // ',
        modelDetailsTop,
        modelDetailsLeft,
        this.modelDetailsProps.width,
        0,
        this.modelDetailsProps.fontSize,
        this.modelDetailsProps.fontWeight,
        'left',
        '#ffffff',
      )
      slideObjects.push(modelDetailsTextObject)
      ttlSlides++
      promises.push(this.createSlide(merch, `${firstArticle.ModelName} - ${padStart(ttlSlides.toString(), 3, '0')}`, slideObjects.slice(), options, this.imageDefaultScaleFactor)
        .catch((error) => {
          console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
        }))

      slideObjects = []
    }

    return Promise.all(promises)
      .then(() => {
        return ttlSlides
      })
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const textDimensions = utils.measureTextBox(text, fontSize, font, fontWeight, availableWidth)
    return utils.getTextHeight(textDimensions.lines ? textDimensions.lines.join('\n') : '', fontSize, font, fabric)
  }

  createLabel(articleId, attributes, articlePropsSeparator, top, left, width, height, fontSize, fontWeight, textAlign, color) {
    if (!Array.isArray(attributes)) {
      attributes = [attributes]
    }
    if (!attributes || attributes.length === 0) { return }

    return {
      type: 'articleDetails',
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill: color,
      flipX: false,
      flipY: false,
      customOptions: {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
        separator: articlePropsSeparator,
      },
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      articleId,
    }
  }

  async getHeaderLogosData(urls: string[]) {
    const loadImage = (url: string) => {
      return new Promise<string>((resolve) => {
        axios.get(url, { responseType: 'arraybuffer' }).then((res) => {
          const base64 = res.data ? `data:image/png;base64,${utils.arrayBufferToBase64(res.data)}` : null
          if (base64) {
            const settings = {
              width: this.logoWidth,
              height: this.logoHeight,
              scaleX: this.logoScaling,
              scaleY: this.logoScaling,
            }
            const image = new Image()
            image.onload = () => {
              resolve(utils.resizeImageAndGetBase64(image, settings) as string)
            }
            image.onerror = () => resolve('')
            image.src = base64
          }
          else {
            resolve('')
          }
        }).catch((err) => {
          console.log(err)
          resolve('')
        })
      })
    }

    const imagePromises = urls.map(loadImage)
    return Promise.all(imagePromises)
  }

  createSlide(merch, slideName, objects: any[], options: Record<string, any>, imageDefaultScaleFactor: number, articleIdList: number[] = []) {
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === slideName))) {
      let c = 0
      do {
        c++
        slideName = `${slideName} (${c})`
      } while (folderNode.children.find(node => node.label === slideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, slideName, objects, options.slideSize, imageDefaultScaleFactor, sortOrder, true, articleIdList, 'rgba(0, 0, 0, 1)')
  }

  changePropsAsPerSlideSize(slideSize: string) { // , numberOfColumns: number
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
    // this.remainingHeight = this.slideHeight - this.yAxisProp.rowTop
    // this.xAxisProp.columnWidth = (this.slideWidth - this.xAxisProp.leftMargin) / numberOfColumns
  }

  async createHeaderLogo(top: number, left: number) {
    return {
      type: 'image',
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderIconLogoData,
    }
  }

  async createHeaderNameLogo(top: number, left: number) {
    return {
      type: 'image',
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      scaleY: this.uaNameLogoScaling,
      scaleX: this.uaNameLogoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderNameLogoData,
    }
  }

  createImage(articleId, left, top, scaleX, scaleY) {
    return {
      type: 'articleImage',
      top,
      left,
      width: this.imageWidth,
      height: this.imageHeight,
      articleId,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      shouldAllowTemplateBigImagesToResize: true,
    }
  }
}

export default new GraphicTeeTechSheet()
